// INAKTIVÁLÁS-FIGYELMEZTETÉS: mi történik a NYITOTT számlázás-sorokkal, ha az ügyfelet/fiókot inaktiválom.
//
// ── MIÉRT VAN EZ ────────────────────────────────────────────────────────────────────────────────────────
// A számla-generátor esedékesség-kapuja korábban `active` tulajdonost kért (ma a záró dátumig számláz:
// `common/utils/billing-end-rule.ts` `ownerStatusBlocks`). Az inaktiválás ezért CSENDBEN elvágta a
// számlázást — és vele az UTÓLAG fizetett sorok UTOLSÓ, már TELJESÍTETT időszakát is: a szeptemberi munka
// októberi számlája sosem készült el. A BFF ezt úgy oldja meg, hogy inaktiváláskor LEZÁRJA a nyitott
// számlázás-sorokat a mai nappal (`clients.tx.ts` / `accounts.tx.ts` → `closeOpenAssignments`): a lezárás
// épp ezt az egy kört még kiengedi, és utána véget is ér.
//
// A felhasználónak viszont a mentés ELŐTT tudnia kell, hogy ez fog történni — és hányszor megy még ki
// számla. Ez a doboz azt mondja meg.
//
// ── MENNYI AZ „X" ───────────────────────────────────────────────────────────────────────────────────────
// Az ELŐRE fizetett (prepaid) sor a lezárás hónapjáig ki van fizetve → nincs több számla. Az UTÓLAG fizetett
// (postpaid) sorra a záró időszakot a KÖVETKEZŐ körben számlázzuk: havinál a következő hónapban, hosszabb
// ciklusnál a periódus lezárultakor. A doboz ezért a postpaid sorokat számolja külön.
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { type TFunction } from 'i18next';

import { Text } from '@/components/ui/text';
import { daysUntilDay } from '@/lib/format-datetime';

/** A figyelmeztetéshez szükséges minimális hozzárendelés-alak (a lista-sor és a detail is ilyet ad). */
export interface DeactivationNoticeRow {
  type: string;
  billingEndDate: string | null;
  billingAgreement: string | null;
}

/**
 * Az inaktiválás által LEZÁRT számlázás-sor: számlázandó típus, záró dátum NÉLKÜL vagy MA UTÁNI záró dátummal — a
 * BFF mindkettőt a mai nappal zárja (`closesOnDeactivation`), különben egy határozott idejű szerződés a jövőbeli
 * záró dátumáig számlázódna egy megszűnt ügyfélnek.
 */
export function openBillingRows<T extends DeactivationNoticeRow>(rows: readonly T[]): T[] {
  return rows.filter((r) => {
    if (r.type !== 'billing') return false;
    if (r.billingEndDate === null || r.billingEndDate === '') return true;
    return (daysUntilDay(r.billingEndDate.slice(0, 10)) ?? 0) > 0;
  });
}

export function DeactivationBillingNotice({
  rows,
  t,
}: {
  /** A tulajdonos ÖSSZES hozzárendelése (a szűrés itt belül történik). */
  rows: readonly DeactivationNoticeRow[];
  t: TFunction;
}): JSX.Element | null {
  const nyitott = openBillingRows(rows);
  if (nyitott.length === 0) return null;
  const utolag = nyitott.filter((r) => r.billingAgreement === 'postpaid').length;

  return (
    <div className="border-warning/40 bg-warning-soft flex gap-2 rounded-lg border p-3">
      <ExclamationTriangleIcon className="text-warning mt-0.5 size-4 shrink-0" aria-hidden />
      <div className="space-y-1">
        <Text variant="secondary" as="p" className="font-medium">
          {t('clients.deactivationNotice.title', { count: nyitott.length })}
        </Text>
        <Text tone="tertiary" as="p">
          {utolag > 0
            ? t('clients.deactivationNotice.postpaid', { count: utolag })
            : t('clients.deactivationNotice.prepaidOnly')}
        </Text>
      </div>
    </div>
  );
}
